import { createSelector } from 'reselect'

import { DATE_VALUES, getParsedDateUntilToday, getRangeFromDateFilter } from 'utils/dateUtils'
import {
  cutArrayToTopAndBottom,
  formatSecondsToDuration,
  normalizePercent,
  normalizeReportData,
  sum,
} from 'utils/helpers'

// `dashboard` is injected only while a Dashboard route is mounted and dropped
// again once another route takes over; in-flight subscriptions/epics can
// still read state in that window, so slice access must be null-safe (see
// AwarenessCenter/Campaigns/selectors.js precedent). The EMPTY_AWARENESS_DATA
// fallback is hoisted (not a fresh literal per call) so selectData's
// createSelector memoization stays stable while the slice is absent.
const EMPTY_OBJ = {}
const EMPTY_ARRAY = []
const EMPTY_AWARENESS_DATA = { reportData: EMPTY_OBJ, campaigns: EMPTY_ARRAY, withcustomIds: false }
const selectAwareness = (state) => state.dashboard?.awareness?.self ?? EMPTY_OBJ

const selectStateData = (state) => selectAwareness(state).data ?? EMPTY_AWARENESS_DATA
const selectLoader = (state) => selectAwareness(state).loader ?? false
const selectError = (state) => selectAwareness(state).error ?? null
// DATE_VALUES.ALL_TIME is null, so a stored null dateFilter is a real user
// choice ("All times") — default only when the slice itself is absent.
const selectTimeFilter = (state) => {
  const slice = selectAwareness(state)
  return 'dateFilter' in slice ? slice.dateFilter : DATE_VALUES.DAYS_BACK_90
}
const selectCacheKey = (state) => selectAwareness(state).cacheKey ?? null
const selectFeDateRange = (state) => selectAwareness(state).dateRange
const selectSelectedCampaignIds = (state) => selectStateData(state).selectedCampaignIds

const selectDateRange = createSelector(
  selectFeDateRange,
  selectTimeFilter,
  (feDateRange, dateFilter) => feDateRange || getRangeFromDateFilter(dateFilter)
)

const selectParsedDate = createSelector(selectTimeFilter, (currentDateFilter) =>
  getParsedDateUntilToday(currentDateFilter)
)

const selectData = createSelector(selectStateData, (stateData) => {
  let rd = stateData.reportData
  let bestReportTimeText = 'N/A'
  let avgReportTimeText = 'N/A'
  const selectedCampaignList = stateData.campaigns
    .map((elem) => ({ value: elem.id, label: elem.name }))
    .filter((campaign) => stateData.selectedCampaignIds?.includes(campaign.value))
  if (Object.keys(rd).length) {
    const nrd = normalizeReportData(stateData.reportData)
    if (nrd.firstReport) {
      const filteredReportTimes = nrd.firstReport.filter((t) => !!t)
      if (filteredReportTimes.length) {
        const bestReportTime = Math.min(...filteredReportTimes)
        const avgReportTime = Math.floor(
          filteredReportTimes.reduce(sum, 0) / filteredReportTimes.length
        )
        bestReportTimeText = formatSecondsToDuration(bestReportTime)
        avgReportTimeText = formatSecondsToDuration(avgReportTime)
      }
    }
    const deptsClickRates = Object.keys(nrd.deptsClickRates)
      .filter((k) => nrd.deptsClickRates[k])
      .sort((k1, k2) => nrd.deptsClickRates[k1] - nrd.deptsClickRates[k2])
      .map((k) => ({
        name: k,
        value: normalizePercent(nrd.deptsClickRates[k]),
      }))

    const scenarioClickFrequency = Object.keys(nrd.scenarioClickFrequency)
      .filter((k) => nrd.scenarioClickFrequency[k])
      .sort((k1, k2) => nrd.scenarioClickFrequency[k1] - nrd.scenarioClickFrequency[k2])
      .map((k) => ({
        name: k,
        value: normalizePercent(nrd.scenarioClickFrequency[k]),
      }))
      .reverse()
    rd = {
      ...nrd,
      deptsClickRates: cutArrayToTopAndBottom(deptsClickRates),
      scenarioClickFrequency: cutArrayToTopAndBottom(scenarioClickFrequency),
      clicksPerLocationOverall: Object.keys(nrd.clicksPerLocationOverall)
        .filter((k) => nrd.clicksPerLocationOverall[k])
        .sort((k1, k2) => nrd.clicksPerLocationOverall[k1] - nrd.clicksPerLocationOverall[k2])
        .map((k) => ({
          name: k,
          value: nrd.clicksPerLocationOverall[k],
        }))
        .reverse(),
    }
  }

  return {
    ...stateData,
    campaigns: stateData.campaigns.map((elem) => ({
      value: elem.id,
      label: elem.name,
    })),
    selectedCampaignList,
    reportData: rd,
    bestReportTimeText,
    avgReportTimeText,
  }
})

export {
  selectData,
  selectLoader,
  selectError,
  selectCacheKey,
  selectTimeFilter,
  selectParsedDate,
  selectDateRange,
  selectSelectedCampaignIds,
}
